import React, { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import './CharacterWritingCanvas.css';

interface CharacterWritingCanvasProps {
  character: string;
  showTemplate: boolean;
  brushSize: number;
  templateFont?: string;
  className?: string;
}

export interface CharacterWritingCanvasRef {
  clear: () => void;
  getImageData: () => string | null;
  hasDrawing: () => boolean;
}

const CharacterWritingCanvas = forwardRef<CharacterWritingCanvasRef, CharacterWritingCanvasProps>(({
  character,
  showTemplate,
  brushSize,
  templateFont = '200px sans-serif',
  className = ''
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const templateCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);

  const CANVAS_SIZE = 300;

  // Initialize canvas
  useEffect(() => {
    if (!character) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Use setTimeout to ensure canvas is fully rendered in DOM
    const timer = setTimeout(() => {
      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;

      const ctx = canvas.getContext('2d', { willReadFrequently: false });
      if (!ctx) return;

      // Store context in ref
      contextRef.current = ctx;

      ctx.strokeStyle = '#333333';
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    }, 0);

    return () => clearTimeout(timer);
  }, [brushSize, character]);

  // Update brush size
  useEffect(() => {
    const ctx = contextRef.current;
    if (!ctx) return;

    ctx.strokeStyle = '#333333';
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [brushSize]);

  // Draw template character
  useEffect(() => {
    const templateCanvas = templateCanvasRef.current;
    if (!templateCanvas || !character) return;

    templateCanvas.width = CANVAS_SIZE;
    templateCanvas.height = CANVAS_SIZE;

    const ctx = templateCanvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    
    if (showTemplate) {
      ctx.fillStyle = 'rgba(51, 51, 51, 0.25)';
      ctx.font = templateFont;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(character, CANVAS_SIZE / 2, CANVAS_SIZE / 2);
    }
  }, [character, showTemplate, templateFont]);

  const getCoordinates = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Handle touch events - check touches first, then changedTouches for touchend
    if ('touches' in e && e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    
    // Handle changedTouches for touchend/touchcancel events
    if ('changedTouches' in e && e.changedTouches && e.changedTouches.length > 0) {
      return {
        x: (e.changedTouches[0].clientX - rect.left) * scaleX,
        y: (e.changedTouches[0].clientY - rect.top) * scaleY,
      };
    }
    
    // Handle mouse events
    if ('clientX' in e) {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }

    return { x: 0, y: 0 };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const ctx = contextRef.current;
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    isDrawingRef.current = true;
  }, [getCoordinates]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();

    const ctx = contextRef.current;
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [getCoordinates]);

  const stopDrawing = useCallback(() => {
    isDrawingRef.current = false;
  }, []);

  const clearCanvas = useCallback(() => {
    const ctx = contextRef.current;
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  }, []);

  const getImageData = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL('image/png');
  }, []);

  const hasDrawing = useCallback(() => {
    const ctx = contextRef.current;
    if (!ctx) return false;

    const imageDataObj = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    const data = imageDataObj.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (r < 255 || g < 255 || b < 255) {
        return true;
      }
    }
    return false;
  }, []);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    clear: clearCanvas,
    getImageData,
    hasDrawing
  }));

  return (
    <div className={`character-writing-canvas-wrapper ${className}`}>
      <canvas
        ref={canvasRef}
        className="character-writing-canvas"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={(e) => {
          e.preventDefault();
          if (e.touches.length === 1) {
            startDrawing(e);
          }
        }}
        onTouchMove={(e) => {
          e.preventDefault();
          if (e.touches.length === 1) {
            draw(e);
          }
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          stopDrawing();
        }}
        onTouchCancel={(e) => {
          e.preventDefault();
          stopDrawing();
        }}
      />
      <canvas
        ref={templateCanvasRef}
        className="character-writing-canvas-template"
      />
    </div>
  );
});

CharacterWritingCanvas.displayName = 'CharacterWritingCanvas';

export default CharacterWritingCanvas;

